import { useState, useCallback, useEffect, useRef } from "react";
import { loadCart, saveCart, clearCart as sbClearCart } from "@/services/supabaseService";
import type { Product } from "@/mocks/products";
import { useAuth } from "@/hooks/useAuth";

export interface CartItem {
  productId: string;
  product: Product;
  quantity: number;
}

// ===== Global shared cart state =====
let _globalItems: CartItem[] = loadCart();
const _listeners = new Set<(items: CartItem[]) => void>();

function _persistAndNotify(items: CartItem[]) {
  _globalItems = items;
  saveCart(items);
  _listeners.forEach((cb) => cb(items));
}

export function useCart() {
  const { user } = useAuth();
  const [items, setItems] = useState<CartItem[]>(_globalItems);
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    setItems(_globalItems); // sync on mount
    const cb = (next: CartItem[]) => {
      if (mountedRef.current) setItems(next);
    };
    _listeners.add(cb);
    return () => {
      mountedRef.current = false;
      _listeners.delete(cb);
    };
  }, []);

  const addToCart = useCallback((product: Product, quantity: number = 1) => {
    const maxStock = product.stock > 0 ? product.stock : 0;

    // 普通分銷全站限購 10 組
    if (user?.level === "普通分銷") {
      const currentTotal = _globalItems.reduce((sum, i) => sum + i.quantity, 0);
      const maxAdd = Math.max(0, 10 - currentTotal);
      if (quantity > maxAdd) {
        return { success: false, message: "普通分銷全站限購 10 組商品" };
      }
    }

    const existing = _globalItems.find((item) => item.productId === product.id);
    let next: CartItem[];
    if (existing) {
      const desiredQty = existing.quantity + quantity;
      const finalQty = Math.min(desiredQty, maxStock);
      next = _globalItems.map((item) =>
        item.productId === product.id
          ? { ...item, quantity: finalQty }
          : item
      );
    } else {
      const finalQty = Math.min(quantity, maxStock);
      next = [..._globalItems, { productId: product.id, product, quantity: finalQty }];
    }
    _persistAndNotify(next);
    return { success: true };
  }, [user]);

  const removeFromCart = useCallback((productId: string) => {
    const next = _globalItems.filter((item) => item.productId !== productId);
    _persistAndNotify(next);
  }, []);

  const updateQuantity = useCallback((productId: string, quantity: number) => {
    const item = _globalItems.find((i) => i.productId === productId);
    const maxStock = item && item.product.stock > 0 ? item.product.stock : 999;
    let clamped = quantity <= 0 ? 0 : Math.min(maxStock, quantity);
    let wasLimited = false;

    // 普通分銷全站限購 10 組
    if (user?.level === "普通分銷" && clamped > 0) {
      const currentTotal = _globalItems.reduce((sum, i) => sum + i.quantity, 0);
      const currentItemQty = item ? item.quantity : 0;
      const otherTotal = currentTotal - currentItemQty;
      const maxAllowedForThisItem = Math.max(0, 10 - otherTotal);
      if (clamped > maxAllowedForThisItem) {
        clamped = maxAllowedForThisItem;
        wasLimited = true;
      }
    }

    let next: CartItem[];
    if (clamped <= 0) {
      if (item && wasLimited) {
        // 因全站限制導致數量歸零，保留原數量不刪除
        next = _globalItems.map((i) =>
          i.productId === productId ? { ...i, quantity: item.quantity } : i
        );
      } else {
        next = _globalItems.filter((item) => item.productId !== productId);
      }
    } else {
      next = _globalItems.map((item) =>
        item.productId === productId ? { ...item, quantity: clamped } : item
      );
    }
    _persistAndNotify(next);
    return { success: true, wasLimited, message: wasLimited ? "普通分銷全站限購 10 組商品" : undefined };
  }, [user]);

  const clearCart = useCallback(() => {
    sbClearCart();
    _persistAndNotify([]);
  }, []);

  const totalCount = items.reduce((sum, item) => sum + item.quantity, 0);

  const totalAmount = items.reduce((sum, item) => {
    const price = getPriceForUser(item.product, user);
    return sum + Math.round(price * item.quantity);
  }, 0);

  const totalSuggested = items.reduce(
    (sum, item) => sum + Math.round(item.product.suggestedPrice * item.quantity),
    0
  );

  const totalProfit = totalSuggested - totalAmount;

  const totalSubscriptionAmount = items.reduce((sum, item) => {
    return sum + Math.round(item.product.distributionPrice * item.quantity);
  }, 0);

  const totalSalesRefund = items.reduce((sum, item) => {
    return sum + Math.round(item.product.suggestedPrice * item.quantity);
  }, 0);

  return {
    items,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    totalCount,
    totalAmount,
    totalSuggested,
    totalProfit,
    totalSubscriptionAmount,
    totalSalesRefund,
  };
}

function getPriceForUser(product: Product, user: import("@/lib/supabase").Profile | null): number {
  if (!user || user.role === "admin") return product.distributionPrice;
  switch (user.level) {
    case "核心分銷":
      return product.corePrice;
    case "特約經銷":
      return product.dealerPrice;
    case "普通分銷":
      return product.normalPrice;
    default:
      return product.distributionPrice;
  }
}